import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Card } from '../ui/Card';
import { theme } from '../../styles/theme';

const AboutSection = styled.section`
  padding: ${theme.spacing['5xl']} 0;
  background: ${theme.colors.white};
  scroll-margin-top: 80px; /* Account for fixed navbar */
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['4xl']};
`;

const SectionTitle = styled(motion.h2)`
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${theme.typography.sizes.xl};
  color: ${theme.colors.gray[600]};
  max-width: 700px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  gap: ${theme.spacing['4xl']};
  margin-bottom: ${theme.spacing['4xl']};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing['2xl']};
  }
`;

const StoryCard = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.neutral} 0%, rgba(165, 167, 221, 0.1) 100%);
`;

const StoryTitle = styled.h3`
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
  font-size: ${theme.typography.sizes['2xl']};
`;

const StoryText = styled.p`
  color: ${theme.colors.gray[700]};
  line-height: ${theme.typography.lineHeights.relaxed};
  margin-bottom: ${theme.spacing.lg};
  
  &:last-child {
    margin-bottom: 0;
  }
`;



export const AboutUs: React.FC = () => {
  return (
    <AboutSection id="about">
      <Container>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            About Us
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            At Care++, we believe better care starts with better communication.
          </SectionSubtitle>
        </SectionHeader>
        
        <ContentGrid>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <StoryCard>
              <StoryTitle>Our Story</StoryTitle>
              <StoryText>
                Our founders witnessed the challenges families and caregivers face when loved ones 
                and patients are under professional care – unclear routines, confusing instructions, 
                missed updates and a general lack of transparency. That’s why we built Care++, a platform 
                connecting caregivers, nurses and families in real time. 
              </StoryText>
              <StoryText>
                With Care++, everyone stays informed, 
                ensuring consistent, high-quality care for those who need it most.
              </StoryText>
            </StoryCard>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <StoryCard>
              <StoryTitle>A Solution for the 21st Century</StoryTitle>
              <StoryText>
                We believe in the power of Artificial Intelligence. 
              </StoryText>
              <StoryText>
                By combining the use of Large Language Models (LLMs) with a process called Retrieval-Augmented Generation (RAG), 
                we finetune the AI model to provide predictive insights 
              </StoryText>
            </StoryCard>
          </motion.div>
        </ContentGrid>
      
        {/*
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <ValuesGrid>
            <motion.div variants={itemVariants}>
              <ValueCard hover>
                <ValueIcon>
                  <Icon icon={FiHeart} />
                </ValueIcon>
                <ValueTitle>Compassionate Care</ValueTitle>
                <ValueDescription>Every feature is designed with empathy, understanding the unique challenges of disability and aged care.</ValueDescription>
              </ValueCard>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <ValueCard hover>
                <ValueIcon>
                  <Icon icon={FiShield} />
                </ValueIcon>
                <ValueTitle>Privacy & Security</ValueTitle>
                <ValueDescription>We prioritize the protection of sensitive patient data with enterprise-grade security measures.</ValueDescription>
              </ValueCard>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <ValueCard hover>
                <ValueIcon>
                  <Icon icon={FiUsers} />
                </ValueIcon>
                <ValueTitle>Collaborative Approach</ValueTitle>
                <ValueDescription>Built through partnerships with care providers, workers, and families to meet real-world needs.</ValueDescription>
              </ValueCard>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <ValueCard hover>
                <ValueIcon>
                  <Icon icon={FiTarget} />
                </ValueIcon>
                <ValueTitle>Continuous Innovation</ValueTitle>
                <ValueDescription>Committed to evolving our platform based on user feedback and emerging healthcare technologies.</ValueDescription>
              </ValueCard>
            </motion.div>
          </ValuesGrid>
        </motion.div>
        
        <StatsContainer>
          <StatItem
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <StatNumber>500+</StatNumber>
            <StatLabel>Hours of Research</StatLabel>
          </StatItem>
          <StatItem
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <StatNumber>50+</StatNumber>
            <StatLabel>Care Professionals Consulted</StatLabel>
          </StatItem>
          <StatItem
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <StatNumber>100%</StatNumber>
            <StatLabel>Privacy Compliant</StatLabel>
          </StatItem>
          <StatItem
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <StatNumber>24/7</StatNumber>
            <StatLabel>Support Commitment</StatLabel>
          </StatItem>
        </StatsContainer>
      */}


      </Container>
    </AboutSection>
  );
};